import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import BACKEND_EN from './backend.en.json';
import { setFormatLanguage } from '../lib/format';

/**
 * Runtime i18n. English is the source language and lives in the code: every UI string is
 * `t(key, englishDefault, values?)`. Other languages are loaded at runtime from the API
 * (`GET /languages` → `GET /i18n/{code}?v={version}`) without a rebuild; missing keys fall back
 * to the English default. Messages are ICU (FormatJS dialect, rich-text tags ignored), which is
 * what the backend validates translations against. `npm run i18n:extract` writes the catalog
 * synced to the backend (`src/i18n/frontend.en.json`).
 */

export type Direction = 'ltr' | 'rtl';
export interface LanguageInfo {
  code: string;
  name: string;
  native_name: string;
  direction: Direction;
  font: string;
  calendar: string;
  active: boolean;
  is_source: boolean;
  completion_pct: number;
  missing_keys: number;
  version: number;
}

interface Active {
  code: string;
  dir: Direction;
  messages: Record<string, string>;
}

const BUILTIN: LanguageInfo[] = [
  {
    code: 'en',
    name: 'English',
    native_name: 'English',
    direction: 'ltr',
    font: 'IBM Plex Sans',
    calendar: 'gregorian',
    active: true,
    is_source: true,
    completion_pct: 100,
    missing_keys: 0,
    version: 0,
  },
  {
    code: 'fa',
    name: 'Persian',
    native_name: 'فارسی',
    direction: 'rtl',
    font: 'Vazirmatn',
    calendar: 'persian',
    active: true,
    is_source: false,
    completion_pct: 0,
    missing_keys: 0,
    version: 0,
  },
  {
    code: 'ar',
    name: 'Arabic',
    native_name: 'العربية',
    direction: 'rtl',
    font: 'IBM Plex Sans Arabic',
    calendar: 'gregorian',
    active: true,
    is_source: false,
    completion_pct: 0,
    missing_keys: 0,
    version: 0,
  },
];

let active: Active = { code: 'en', dir: 'ltr', messages: {} };
const compiled = new Map<string, IntlMessageFormat | null>();
const STORAGE_KEY = 'bx.lang';
const API = '/api/v1';

type AstNode = { type: number; value?: string; options?: Record<string, { value: AstNode[] }> };
const FSI = '\u2068';
const PDI = '\u2069';
/** The marks that `rich` splits a message at: they stay bare, or a split would leave an open isolate. */
const MARKS = /[\ue000-\uf8ff]/;

/** Names of the plain `{value}` placeholders; plural and select need the bare value to choose. */
function plainArgs(nodes: AstNode[], out: Set<string> = new Set()): Set<string> {
  for (const n of nodes) {
    if (n.type === 1 && n.value) out.add(n.value);
    if (n.options) for (const o of Object.values(n.options)) plainArgs(o.value, out);
  }
  return out;
}

/** A `{x, number}` (2) or plural (6) given a value that is no number: Intl would print NaN in the language. */
function badNumber(nodes: AstNode[], values: Record<string, unknown>): boolean {
  return nodes.some(
    (n) =>
      ((n.type === 2 || n.type === 6) && n.value != null && Number.isNaN(Number(values[n.value]))) ||
      (n.options != null && Object.values(n.options).some((o) => badNumber(o.value, values))),
  );
}

/** Names a select (5) or plural (6) chooses on, at any depth. */
function choosingArgs(nodes: AstNode[], out: Set<string> = new Set()): Set<string> {
  for (const n of nodes) {
    if ((n.type === 5 || n.type === 6) && n.value) out.add(n.value);
    if (n.options) for (const o of Object.values(n.options)) choosingArgs(o.value, out);
  }
  return out;
}

/**
 * In a right-to-left sentence a value keeps its own direction: `$17,027` stays `$17,027` instead of
 * moving its `$` behind the digits, and a Latin name keeps its punctuation. A value that a select or
 * plural chooses on stays bare even when a branch prints it: `{k, select, a {x} other {{k}}}` would
 * otherwise match no case.
 */
function isolated(f: IntlMessageFormat, values: Record<string, unknown>): Record<string, unknown> {
  const ast = f.getAst() as unknown as AstNode[];
  const plain = plainArgs(ast);
  for (const k of choosingArgs(ast)) plain.delete(k);
  const out = { ...values };
  for (const k of plain) {
    const v = out[k];
    if (typeof v === 'string' && v && !MARKS.test(v) && !v.includes('\u0000')) out[k] = FSI + v + PDI;
  }
  return out;
}

function format(code: string, key: string, msg: string, values?: Record<string, unknown>, failed = msg): string {
  if (!values && !/[{}'#]/.test(msg)) return msg;
  const ck = code + '\u0000' + key + '\u0000' + msg;
  let f = compiled.get(ck);
  if (f === undefined) {
    try {
      // Latin digits in every language, like lib/format.ts
      f = new IntlMessageFormat(msg, code + '-u-nu-latn', undefined, { ignoreTag: true });
    } catch {
      f = null;
    }
    compiled.set(ck, f);
  }
  if (!f) return failed;
  try {
    if (values && badNumber(f.getAst() as unknown as AstNode[], values)) return failed;
    const v = values && code !== 'en' && active.dir === 'rtl' ? isolated(f, values) : values;
    const r = f.format(v as Record<string, string | number> | undefined);
    return Array.isArray(r) ? r.join('') : String(r);
  } catch {
    return failed;
  }
}

/** Translate `key`; `def` is the English source text (ICU). */
export function t(key: string, def: string, values?: Record<string, unknown>): string {
  const msg = active.messages[key];
  if (msg != null && msg !== '') return format(active.code, key, msg, values);
  return format('en', key, def, values);
}

/**
 * Translate a key owned by the backend catalog (kpi.*, alerts.*, errors.*, reports.*,
 * needs_config.*, completeness.*, actions.*, ai.*): read from the runtime bundle, with an English
 * fallback while the bundle is not loaded. Not extracted into the panel's catalog.
 */
export function tb(key: string, fallback: string, values?: Record<string, unknown>): string {
  // params that don't fit the message (an older row) show the English that came with them, never ICU source
  const msg = active.messages[key];
  if (msg != null && msg !== '') return format(active.code, key, msg, values, fallback);
  const en = (BACKEND_EN as Record<string, string>)[key];
  return format('en', key, en ?? fallback, values, fallback);
}

/** A piece of generated text from the API: its English, catalog key and raw values. */
export interface TextItem {
  text: string;
  key?: string | null;
  params?: Record<string, unknown> | null;
}

/**
 * A text the backend generated: translated from its key and raw values. Rows written before the keys
 * existed have only the English, which also stands in for a key the bundle lacks.
 */
export function tx(english: string | null | undefined, key?: string | null, params?: Record<string, unknown> | null): string {
  if (english == null) return '';
  return key ? tb(key, english, params ?? undefined) : english;
}

/** `tf(alert, 'title')`: the API's convention for a text field `x` with `x_key` and `x_params`. */
export function tf(o: object | null | undefined, name: string): string {
  const r = (o ?? {}) as Record<string, never>;
  return tx(r[name], r[name + '_key'], r[name + '_params']);
}

/** Text joined from parts (`x_items`): each part translated, joined with the separator the contract states. */
export function txItems(items: TextItem[] | null | undefined, sep = '; '): string {
  return (items ?? []).map((i) => tx(i.text, i.key, i.params)).join(sep);
}

/** The active language code and direction (for code outside React). */
export function currentLanguage(): { code: string; dir: Direction } {
  return { code: active.code, dir: active.dir };
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function store(code: string) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* storage unavailable */
  }
}

interface I18nCtx {
  code: string;
  dir: Direction;
  languages: LanguageInfo[];
  loading: boolean;
  setLanguage: (code: string) => Promise<void>;
  reload: () => Promise<void>;
}
const Ctx = createContext<I18nCtx>({
  code: 'en',
  dir: 'ltr',
  languages: BUILTIN,
  loading: false,
  setLanguage: async () => {},
  reload: async () => {},
});

export function useI18n(): I18nCtx {
  return useContext(Ctx);
}

async function fetchLanguages(): Promise<LanguageInfo[]> {
  const r = await fetch(API + '/languages', { credentials: 'include' });
  if (!r.ok) throw new Error('languages ' + r.status);
  const j = (await r.json()) as { items: LanguageInfo[] };
  return j.items.filter((l) => l.active || l.is_source);
}

async function fetchBundle(code: string, version: number): Promise<{ direction: Direction; messages: Record<string, string> }> {
  const r = await fetch(API + '/i18n/' + encodeURIComponent(code) + '?v=' + version, { credentials: 'include' });
  if (!r.ok) throw new Error('bundle ' + r.status);
  return r.json();
}

export function applyDocumentLanguage(code: string, dir: Direction) {
  const el = document.documentElement;
  el.setAttribute('lang', code);
  el.setAttribute('dir', dir);
}

export function I18nProvider({ children, initial }: { children: React.ReactNode; initial?: string }) {
  const [languages, setLanguages] = useState<LanguageInfo[]>(BUILTIN);
  const languagesRef = React.useRef(languages);
  languagesRef.current = languages;
  const [state, setState] = useState<{ code: string; dir: Direction; rev: number }>({ code: 'en', dir: 'ltr', rev: 0 });
  const [loading, setLoading] = useState(false);
  // the app renders once the first language is applied, so loading it never remounts a page the
  // user is already typing in; later language switches remount the tree on purpose
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const tm = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(tm);
  }, []);

  const load = useCallback(async (code: string, langs: LanguageInfo[]) => {
    const info = langs.find((l) => l.code === code) || BUILTIN.find((l) => l.code === code);
    if (!info || code === 'en') {
      // English texts of the panel are in the code; the bundle adds the backend-owned messages
      let messages: Record<string, string> = {};
      try {
        messages = (await fetchBundle('en', info?.version ?? 0)).messages || {};
      } catch {
        /* API unreachable: code defaults only */
      }
      active = { code: 'en', dir: 'ltr', messages };
      compiled.clear();
      setFormatLanguage('en');
      applyDocumentLanguage('en', 'ltr');
      setState((s) => ({ code: 'en', dir: 'ltr', rev: s.rev + 1 }));
      store('en');
      return;
    }
    setLoading(true);
    try {
      const b = await fetchBundle(code, info.version);
      active = { code, dir: b.direction || info.direction, messages: b.messages || {} };
    } catch {
      // API unreachable: switch direction and fonts, keep English texts
      active = { code, dir: info.direction, messages: {} };
    } finally {
      setLoading(false);
    }
    compiled.clear();
    setFormatLanguage(active.code, info.calendar, active.dir);
    applyDocumentLanguage(active.code, active.dir);
    setState((s) => ({ code: active.code, dir: active.dir, rev: s.rev + 1 }));
    store(code);
  }, []);

  // Refresh the language list (e.g. after LCH-02 activates a language). The tree is remounted only
  // when the active language's bundle changed, so pages keep their state otherwise.
  const reload = useCallback(async () => {
    let langs: LanguageInfo[];
    try {
      langs = await fetchLanguages();
    } catch {
      return;
    }
    const before = languagesRef.current.find((l) => l.code === active.code)?.version;
    const after = langs.find((l) => l.code === active.code)?.version;
    setLanguages(langs);
    if (before !== after) await load(active.code, langs);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let langs = BUILTIN;
      try {
        langs = await fetchLanguages();
        if (!cancelled) setLanguages(langs);
      } catch {
        /* offline or API not running */
      }
      const want = initial || readStored() || 'en';
      if (!cancelled) await load(want, langs);
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [initial, load]);

  const value = useMemo<I18nCtx>(
    () => ({ code: state.code, dir: state.dir, languages, loading, setLanguage: (c: string) => load(c, languages), reload }),
    [state.code, state.dir, languages, loading, load, reload],
  );

  // Re-mount the tree on a language change so class components re-render their t() texts.
  return (
    <Ctx.Provider value={value}>
      {ready ? <React.Fragment key={state.code + ':' + state.rev}>{children}</React.Fragment> : null}
    </Ctx.Provider>
  );
}

/** For tests: activate messages synchronously. */
export function __setMessagesForTest(code: string, dir: Direction, messages: Record<string, string>) {
  active = { code, dir, messages };
  compiled.clear();
  setFormatLanguage(code, BUILTIN.find((l) => l.code === code)?.calendar, dir);
}
